<template>
  <Loader v-if="loader" />
  <h3>Info Punti Distacco</h3>

  <div class="filters-container">
    <div class="actions-row">
      <div class="year-buttons">
        <Button 
          v-for="year in availableYears" 
          :key="year"
          :label="'Anno ' + year"
          :class="{ 'p-button-outlined': selectedYear !== year }"
          @click="selectedYear = year"
          class="mr-2"
        />
      </div>
        <Button
          icon="pi pi-download"
        label="Esporta Excel" 
        @click="exportToCSV"
        class="p-button-success"
        />
      </div>

    <div class="p-input-filled">
      <span class="p-input-icon-left">
        <i class="pi pi-search" />
        <InputText v-model="searchPod" placeholder="Cerca POD" class="w-full"/>
      </span>
    </div>
  </div>

  <DataView
    v-if="loaded_items"
    :value="Object.entries(filteredByYear)"
    :paginator="true"
    :rows="50"
    :rowsPerPageOptions="[50, 100, 200]"
    layout="list"
  >
    <template #list="slotProps">
      <div class="year-container" v-for="[year, yearData] in slotProps.items" :key="year">
        <h4 class="year-header">Anno {{ year }}</h4>
        <div class="panels-container">
          <Panel 
            v-for="[pod, group] in Object.entries(yearData)" 
            :key="`${year}-${pod}`" 
            :toggleable="true"
            :collapsed="true"
          >
            <template #header>
              <div class="panel-header">
                <span class="pod-code">{{ pod }}</span>
                <span class="esiti-summary">
                  ({{ getEsitiSummary(group) }})
                </span>
              </div>
          </template>
            <DataTable
              :value="group"
              tableStyle="min-width: 50rem"
              v-model:filters="filters"
              filterDisplay="menu"
              responsiveLayout="scroll"
            >
              <Column field="data_pratica" header="Data Pratica" :sortable="true">
                <template #body="{ data }">
                  {{ formatDate(data.data_pratica) }}
                </template>
                <template #filter="{ filterModel }">
                  <Calendar v-model="filterModel.value" dateFormat="dd/mm/yy" placeholder="Cerca per data" />
                </template>
              </Column>

              <Column field="motivazione_esito" header="Motivazione Esito" :sortable="true">
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" placeholder="Cerca per motivazione" />
                </template>
              </Column>

              <Column field="esito_name" header="Esito" :sortable="true">
                      <template #body="{ data }">
                  {{ getEsitoLabel(data.esito_name) }}
                </template>
                <template #filter="{ filterModel }">
                  <Dropdown v-model="filterModel.value" :options="esitoOptions" optionLabel="label" optionValue="value" placeholder="Seleziona esito" />
                      </template>
                    </Column>

              <Column field="data_messainmora" header="Data Messa in Mora" :sortable="true">
                            <template #body="{ data }">
                  {{ formatDate(data.data_messainmora) }}
                            </template>
                          </Column>

              <Column field="data_preavviso" header="Data Preavviso" :sortable="true">
                            <template #body="{ data }">
                  {{ formatDate(data.data_preavviso) }}
                            </template>
                          </Column>

              <Column field="data_sollecito" header="Data Sollecito" :sortable="true">
                            <template #body="{ data }">
                  {{ formatDate(data.data_sollecito) }}
                            </template>
                          </Column>

              <Column field="data_riattiva" header="Data Riattivazione" :sortable="true">
                            <template #body="{ data }">
                  {{ formatDate(data.data_riattiva) }}
                            </template>
                          </Column>
            </DataTable>
          </Panel>
        </div>
          </div>
        </template>
  </DataView>
</template>

<script>
import Service from "@/services/ask-pratiche/pratiche-service";
import { ref, onMounted, getCurrentInstance, computed } from "vue";
import Loader from "@/components/Loader.vue";
import moment from "moment";
import Panel from 'primevue/panel';
import DataView from 'primevue/dataview';
import Button from 'primevue/button';

export default {
  components: {
    Loader,
    Panel,
    DataView,
    Button
  },
  setup() {
    const loader = ref(false);
    const loaded_items = ref(false);
    const items = ref([]);
    const filters = ref({});
    const searchPod = ref('');
    const selectedYear = ref(null);
    
    const self = getCurrentInstance();
    self.$http = self.appContext.config.globalProperties.$http;
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$http, self.$eventBus);

    const esitoOptions = [
      { label: 'Esito 2', value: 2 },
      { label: 'Esito 3', value: 3 }
    ];

    const availableYears = computed(() => {
      const years = new Set();
      items.value.forEach(item => {
        const year = moment(item.data_pratica).year();
        years.add(year);
      });
      return Array.from(years).sort();
    });

    const filteredByYear = computed(() => {
      const filtered = items.value.filter(item => 
        item.pod_code.toLowerCase().includes(searchPod.value.toLowerCase()) &&
        (!selectedYear.value || moment(item.data_pratica).year() === selectedYear.value)
      );

      return filtered.reduce((years, item) => {
        const year = moment(item.data_pratica).year();
        
        if (!years[year]) {
          years[year] = {};
        }
        
        if (!years[year][item.pod_code]) {
          years[year][item.pod_code] = [];
        }
        
        years[year][item.pod_code].push(item);
        return years;
      }, {});
    });

    const getData = async () => {
      loader.value = true;
      try {
        const response = await service.getInfoFromPraticheDossier();
        if (response.items) {
          items.value = response.items;
          loaded_items.value = true;
        }
      } catch (error) {
        console.error("Errore nel caricamento dei dati:", error);
      } finally {
        loader.value = false;
      }
    };

    const formatDate = (value) => {
      if (!value) return '';
      return moment(value).format("DD/MM/YYYY");
    };

    const getEsitoLabel = (esito) => {
      const option = esitoOptions.find(opt => opt.value === esito);
      return option ? option.label : esito;
    };

    const getEsitiSummary = (group) => {
      const summary = group.reduce((acc, item) => {
        acc[item.esito_name] = (acc[item.esito_name] || 0) + 1;
        return acc;
      }, {});

      return Object.entries(summary)
        .map(([esito, count]) => `${count} ${esito}`)
        .join(', ');
    };

    const exportToCSV = () => {
      const headers = [
        'POD',
        'Data Pratica',
        'Motivazione Esito',
        'Esito',
        'Data Messa in Mora',
        'Data Preavviso',
        'Data Sollecito',
        'Data Riattivazione'
      ];

      const dataByYear = items.value.reduce((acc, item) => {
        console.log('Item data:', item);
        const year = moment(item.data_pratica).year();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(item);
        return acc;
      }, {});

      let csvContent = '';
      
      Object.entries(dataByYear).sort().forEach(([year, yearData]) => {
        csvContent += `\nANNO ${year}\n`;
        
        csvContent += headers.join(',') + '\n';
        
        const rows = yearData.map(item => [
          `"${item.pod_code}"`,
          `"${item.data_pratica ? formatDate(item.data_pratica) : 'null'}"`,
          `"${item.motivazione_esito || ''}"`,
          `"${item.esito_name || ''}"`,
          `"${item.data_messainmora ? formatDate(item.data_messainmora) : 'null'}"`,
          `"${item.data_preavviso ? formatDate(item.data_preavviso) : 'null'}"`,
          `"${item.data_sollecito ? formatDate(item.data_sollecito) : 'null'}"`,
          `"${item.data_riattivazione ? formatDate(item.data_riattivazione) : 'null'}"`,
        ].join(','));
        
        csvContent += rows.join('\n') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'Info Punti Distacco.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    onMounted(() => {
      getData().then(() => {
        if (availableYears.value.length > 0) {
          selectedYear.value = availableYears.value[0];
        }
      });
    });

    return {
      items,
      loaded_items,
      loader,
      filters,
      formatDate,
      getEsitoLabel,
      esitoOptions,
      availableYears,
      selectedYear,
      filteredByYear,
      searchPod,
      getEsitiSummary,
      exportToCSV
    };
  },
};
</script>

<style scoped>
.panels-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

:deep(.p-panel) {
  margin-bottom: 0.5rem;
}

:deep(.p-panel-header) {
  padding: 1rem;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pod-code {
  font-weight: bold;
}

.esiti-summary {
  color: #666;
  font-size: 0.9em;
}

:deep(.p-datatable) {
  margin-top: 1rem;
}

:deep(.p-column-filter) {
  width: 100%;
}

.mb-3 {
  margin-bottom: 1rem;
}

.w-full {
  width: 100%;
}

:deep(.p-dataview) {
  width: 100%;
}

:deep(.p-dataview-content) {
  padding: 1rem;
}

:deep(.p-paginator) {
  padding: 1rem;
}

.year-container {
  margin-bottom: 2rem;
}

.year-header {
  font-size: 1.2rem;
  color: #555;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #eee;
}

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.year-buttons {
  display: flex;
  gap: 0.5rem;
}

:deep(.p-button) {
  min-width: 120px;
}

.mr-2 {
  margin-right: 0.5rem;
}

.actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.year-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>

